import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/layout/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { toast } from "sonner";

const PASSWORD_REQUIREMENTS = [
  "Minimálně 6 znaků",
  "Alespoň jedno velké písmeno",
  "Alespoň jedno malé písmeno",
  "Alespoň jedno číslo"
];

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if this is a valid recovery link
  const isRecovery = searchParams.get("type") === "recovery";
  const accessToken = searchParams.get("access_token");

  useEffect(() => {
    if (!isRecovery || !accessToken) {
      toast.error("Neplatný nebo expirovaný odkaz pro obnovení hesla.");
      navigate("/login");
    }
  }, [isRecovery, accessToken, navigate]);

  const validatePassword = () => {
    if (password !== confirmPassword) {
      setError("Hesla se neshodují");
      return false;
    }
    if (password.length < 6) {
      setError("Heslo musí mít alespoň 6 znaků");
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setError("Heslo musí obsahovat alespoň jedno velké písmeno");
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setError("Heslo musí obsahovat alespoň jedno malé písmeno");
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setError("Heslo musí obsahovat alespoň jedno číslo");
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password || !confirmPassword) {
      setError("Prosím vyplňte všechna pole");
      return;
    }

    if (!validatePassword()) {
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) throw error;
      
      setSuccess(true);
      toast.success("Heslo bylo úspěšně změněno!");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      console.error("Reset password error:", err);
      setError("Nepodařilo se změnit heslo. Zkuste to prosím znovu.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4 bg-gray-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Heslo změněno</CardTitle>
              <CardDescription>
                Vaše heslo bylo úspěšně změněno. Budete přesměrováni na přihlašovací stránku...
              </CardDescription>
            </CardHeader>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center p-4 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Nastavení nového hesla</CardTitle>
            <CardDescription>
              Zadejte své nové heslo
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Nové heslo</Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  error={!!error}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Potvrzení hesla</Label>
                <PasswordInput
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  error={!!error}
                />
                <div className="text-sm text-gray-500">
                  <p className="font-medium mb-1">Požadavky na heslo:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {PASSWORD_REQUIREMENTS.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? "Měním heslo..." : "Změnit heslo"}
              </Button>
            </CardContent>
          </form>
        </Card>
      </main>
    </div>
  );
};

export default ResetPasswordPage; 